import * as React from "react";
import {
    ProcessStage,
    ProcessStageArrow,
    ProcessStageArrowDirection,
    ProcessStageOrder,
    useProcessController
} from "../../Processes";
import {Button, TextInput} from "@vebox/etui-core";
import {INPUT_BORDER_BOX} from "../../../components/common/etui_components_styles";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {LoadingButton} from "../../../components/buttons/LoadingButton";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {VeboxOverlayController} from "../../../components/overlay/VeboxOverlay";
import "../packagesClaim/ClaimAndCheck.scss";
import {ValidateEquipment} from "../MassiveActionsTypesAndFunctions";
import {DATA_STATE} from "../../../users/contexts/UsersDashboardContext";
import {Display} from "../../../components/Display";
import {MassiveActionProcessState} from "../MassiveActionsProvider";
import {EquipmentListItem} from "./EquipmentListItem";
import {WarningBanner} from "./WarningBanner";

declare var AppTranslations: AppTranslations;

interface CodesCheckAndApproveProps {
    overlayController: VeboxOverlayController;
    validatedEquipmentsInfo: {
        validatedEquipments: ValidateEquipment[],
        validatedEquipmentState: DATA_STATE
    }
    submitFunction: (validatedEquipmentGuids: string[]) => void
    stepNumber: number
    stageOrder: ProcessStageOrder
    title: string
    cardTitle?: string
    massiveActionProcessState: MassiveActionProcessState
}

const equipmentValidatedReducer = (status: boolean) => (count: number, e: ValidateEquipment) => {
    return count + (e.valid === status ? 1 : 0)
};

const equipmentNotValidReducer = equipmentValidatedReducer(false);
const equipmentValidReducer = equipmentValidatedReducer(true);

export const CodesCheckAndApprove: React.FC<CodesCheckAndApproveProps> = ({
                                                                              validatedEquipmentsInfo,
                                                                              overlayController,
                                                                              submitFunction,
                                                                              stepNumber,
                                                                              title,
                                                                              cardTitle,
                                                                              stageOrder,
                                                                              massiveActionProcessState
                                                                          }) => {
    const processController = useProcessController();
    const texts = AppTranslations.massiveActions.massiveActionsCommon;
    const [equipments, setEquipments] = React.useState<ValidateEquipment[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);

    const [equipmentCodeFilter, setEquipmentCodeFilter] = React.useState<string>("");


    const resetAll = () => {
        setEquipmentCodeFilter("")
        setEquipments([])
        setLoading(false);
        processController.setReachablePosition(0);
    }
    const changePackageCodeFilter = e => setEquipmentCodeFilter(e.target.value);

    const equipmentNotValid: number = equipments?.reduce(equipmentNotValidReducer, 0);
    const equipmentValid: number = equipments?.reduce(equipmentValidReducer, 0);

    const removeNotValid = () => {
        setEquipments(equipments.filter(e => e.valid));
    }

    const removeEquipment = (equipmentCode: string) => {
        setEquipments(equipments.filter(e => e.code !== equipmentCode));
    }

    const executeMassiveAction = () => {
        setLoading(true);
        submitFunction(equipments.map(eq => eq.guid))
    }


    React.useEffect(() => {
        if (validatedEquipmentsInfo?.validatedEquipmentState === DATA_STATE.loaded) {
            setEquipments(validatedEquipmentsInfo?.validatedEquipments);
        }
    }, [validatedEquipmentsInfo?.validatedEquipmentState])
    const openModal = () => overlayController.show();

    React.useEffect(() => {
        if (massiveActionProcessState === MassiveActionProcessState.INITIAL) {
            resetAll();
        }
        if (massiveActionProcessState === MassiveActionProcessState.FAILURE) {
            setLoading(false);
        }
    }, [massiveActionProcessState])

    return (
        <ProcessStage
            stepNumber={stepNumber}
            title={title}
            stageOrder={stageOrder}
        >
            <div className={"flx-row"}>
                <ProcessStageArrow
                    stepNumber={stepNumber}
                    direction={ProcessStageArrowDirection.BACKWARD}
                    visible={true}
                />
                <div className={"codes-check-and-approve-box"}>
                    {
                        ((equipmentNotValid) > 0) &&
                        <WarningBanner
                            equipmentNotValid={equipmentNotValid}
                            onRemove={removeNotValid}
                        />
                    }
                    <VeBoxCard className={"form-card"} style={{maxHeight: "400px", gridRowStart: 2}}>
                        <Display isVisible={cardTitle !== undefined}>
                            <h2 className={"bold card-title"}>{cardTitle}</h2>
                        </Display>
                        <div className={"flx-row"}>
                            <TextInput
                                label={texts.searchCode}
                                styles={{...INPUT_BORDER_BOX, root: {width: "100%"}} as any}
                                autoComplete={"off"}
                                value={equipmentCodeFilter}
                                onChange={changePackageCodeFilter}
                            />
                            <Button onClick={openModal} variant={"tertiary"}>
                                <span className="material-icons-outlined">help_outline</span>
                            </Button>
                        </div>
                        <ul className={"claim-list"}>
                            {
                                equipments?.sort(eq => !eq.valid ? -1 : 1)
                                    .filter(eq => eq.code.includes(equipmentCodeFilter))
                                    .map((eq, i) => (
                                        <EquipmentListItem key={i} equipmentDetail={eq} onRemove={removeEquipment}/>
                                    ))
                            }
                        </ul>
                        <div className={"flx-row flx-space-between"}>
                            <p>
                                <Display isVisible={equipments?.length > 0}>
                                    {equipmentValid} {equipmentValid === 1 ? texts.equipmentFoundLabel : texts.equipmentFoundLabelPlural}, {equipmentNotValid} {equipmentNotValid === 1 ? texts.notFoundSingle : texts.notFoundPlural}
                                </Display>
                            </p>
                            <LoadingButton
                                variant={"strong"}
                                onClick={executeMassiveAction}
                                loading={loading}
                                disabled={equipmentNotValid > 0 || equipments.length === 0}
                                styles={{text: {textTransform: "capitalize"}}}
                            >
                                {texts.confirmOperation}
                            </LoadingButton>
                        </div>
                    </VeBoxCard>
                </div>
                <ProcessStageArrow stepNumber={stepNumber} direction={ProcessStageArrowDirection.FORWARD}/>
            </div>
        </ProcessStage>
    );
}